/**
 * ElementHighlight - Visual overlay to highlight hovered editor elements
 */
import React from 'react'
import { EditorElement, getQuestionType } from 'utils/editorElementDetection'
import { NodeType } from 'utils/structureParser'

interface ElementHighlightProps {
  element: EditorElement
  containerRef: React.RefObject<HTMLDivElement | null>
}

/**
 * Get color scheme based on element type
 */
const getElementColors = (type: NodeType): { border: string; background: string; badge: string } => {
  switch (type) {
    case NodeType.QUESTION:
      return {
        border: '#36C3ED',
        background: 'rgba(54, 195, 237, 0.05)',
        badge: '#36C3ED',
      }
    case NodeType.SECTION:
      return {
        border: '#16A34A',
        background: 'rgba(22, 163, 74, 0.05)',
        badge: '#16A34A',
      }
    case NodeType.LIST:
    case NodeType.RANGE_LIST:
      return {
        border: '#E67E22',
        background: 'rgba(230, 126, 34, 0.05)',
        badge: '#E67E22',
      }
    case NodeType.EXCEPTIONS:
    case NodeType.EXCEPTION_ITEM:
      return {
        border: '#E74C3C',
        background: 'rgba(231, 76, 60, 0.05)',
        badge: '#E74C3C',
      }
    case NodeType.TEXT:
      return {
        border: '#27AE60',
        background: 'rgba(39, 174, 96, 0.05)',
        badge: '#27AE60',
      }
    case NodeType.NOTE:
      return {
        border: '#9B59B6',
        background: 'rgba(155, 89, 182, 0.05)',
        badge: '#9B59B6',
      }
    case NodeType.LOGIC:
      return {
        border: '#95A5A6',
        background: 'rgba(149, 165, 166, 0.05)',
        badge: '#95A5A6',
      }
    default:
      return {
        border: '#BDC3C7',
        background: 'rgba(189, 195, 199, 0.05)',
        badge: '#BDC3C7',
      }
  }
}

/**
 * Get display name for element type
 */
const getElementTypeLabel = (element: EditorElement): string => {
  // For questions, show the specific question type
  if (element.type === NodeType.QUESTION) {
    return getQuestionType(element).toUpperCase()
  }

  // For other types
  switch (element.type) {
    case NodeType.RANGE_LIST:
      return 'RANGE LIST'
    default:
      return element.type.toUpperCase()
  }
}

/**
 * ElementHighlight Component
 */
const ElementHighlight: React.FC<ElementHighlightProps> = ({ element, containerRef }) => {
  const colors = getElementColors(element.type)
  const label = getElementTypeLabel(element)

  // Calculate offset if line numbers are enabled
  const lineNumbersEnabled = containerRef.current?.querySelector('.line-numbers-questionnaire')
  const leftOffset = lineNumbersEnabled ? 40 : 0

  return (
    <div
      className="element-highlight"
      style={{
        position: 'absolute',
        top: element.startY,
        left: leftOffset,
        right: 0,
        height: element.height,
        border: `2px solid ${colors.border}`,
        borderRadius: '4px',
        backgroundColor: colors.background,
        pointerEvents: 'none',
        zIndex: 10,
        transition: 'all 0.15s ease',
        boxShadow: `0 0 0 1px ${colors.border}33`,
      }}
    >
      {/* Element Type Badge */}
      <div
        className="element-type-badge"
        style={{
          position: 'absolute',
          top: -12,
          right: 8,
          background: colors.badge,
          color: 'white',
          padding: '2px 8px',
          borderRadius: '3px',
          fontSize: '10px',
          fontWeight: 600,
          letterSpacing: '0.5px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        }}
      >
        {label}
      </div>

      {/* Placeholder Warning Badge */}
      {element.metadata?.isPlaceholder && (
        <div
          className="element-placeholder-badge"
          style={{
            position: 'absolute',
            top: -12,
            right: element.type === NodeType.QUESTION || element.type === NodeType.LIST || element.type === NodeType.RANGE_LIST ? 120 : 100,
            background: '#E67E22',
            color: 'white',
            padding: '2px 6px',
            borderRadius: '3px',
            fontSize: '9px',
            fontWeight: 600,
            letterSpacing: '0.5px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          }}
        >
          ⚠️ PLACEHOLDER
        </div>
      )}

      {/* Element Name Label (for longer elements) */}
      {element.height > 60 && element.name && (
        <div
          className="element-name-label"
          style={{
            position: 'absolute',
            bottom: 4,
            right: 8,
            color: colors.badge,
            fontSize: '11px',
            fontWeight: 500,
            opacity: 0.7,
            maxWidth: 'calc(100% - 16px)',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {element.name}
        </div>
      )}
    </div>
  )
}

export default ElementHighlight
